// The one RPC between the editor and the extension sandbox (spec/dashboard.md section 10.2): structured-clone messages
// both ways. The sandbox never sees the editor's objects; the editor never sees the extensions' classes.

export type LogLevel = 'info' | 'warn' | 'error';

export interface ScriptSource {
  name: string;
  source: string;
}

export interface MenuItemSpec {
  /** `<extension>.<method>` */
  id: string;
  extension: string;
  method: string;
  /** the path with a leading `Tools/` stripped */
  path: string;
  priority: number;
}

export interface ChangeHandlerSpec {
  extension: string;
  method: string;
  template?: string;
  param?: string;
}

export interface LoadedExtension {
  name: string;
  menu: MenuItemSpec[];
  onChange: ChangeHandlerSpec[];
  hooks: { preSave: boolean; postSave: boolean };
}

export interface LoadError {
  name: string;
  message: string;
  line?: number;
}

export interface ChangedEntity {
  unnyId: string;
  id: string;
  templateName: string;
  changedParams: string[];
  changes: { param: string; before: unknown; after: unknown }[];
  isNew: boolean;
  isDeleted: boolean;
}

export interface PreSaveCtx {
  changedEntities: ChangedEntity[];
}

export interface PostSaveCtx {
  idMapping: Record<string, string>;
  changedEntities: ChangedEntity[];
}

export interface ChangeEvent {
  unnyId: string;
  id: string;
  templateName: string;
  paramName: string;
  newValue: unknown;
  oldValue: unknown;
}

/** One extension's preSave outcome. */
export interface PreSaveOutcome {
  extension: string;
  /** `allow`, or a block naming its entities (every changed entity when `entities` is empty) */
  result: { kind: 'allow' } | { kind: 'block'; reason: string; entities: string[] } | { kind: 'failed'; error: string } | { kind: 'timeout' };
  warnings: string[];
  ms: number;
}

export type HostMessage =
  | { type: 'load'; scripts: ScriptSource[] }
  | { type: 'dispose' }
  | { type: 'menu'; callId: string; id: string }
  | { type: 'preSave'; callId: string; ctx: PreSaveCtx; timeoutMs: number }
  | { type: 'postSave'; callId: string; ctx: PostSaveCtx }
  | { type: 'change'; event: ChangeEvent; handlers: string[] }
  | { type: 'result'; id: string; result?: unknown; error?: string };

export type SandboxMessage =
  | { type: 'ready'; extensions: LoadedExtension[]; errors: LoadError[] }
  | { type: 'log'; level: LogLevel; extension: string; message: string; data?: unknown }
  | { type: 'call'; id: string; method: string; args: unknown[]; extension: string }
  | { type: 'done'; callId: string; outcomes?: PreSaveOutcome[]; error?: string }
  | { type: 'disposed' };

export const PRESAVE_TIMEOUT_MS = 5000;
export const ONCHANGE_DEBOUNCE_MS = 300;
